import React, { useState, useCallback } from 'react';
import { FaTimes, FaSave, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PlantafelService, { LaneGroup, LaneTeam } from '../services/PlantafelService';
import { pdebugError } from '../utils/plantafelDebug';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

interface TeamModalProps {
  workArea: LaneGroup;
  team: LaneTeam | null;
  onClose: () => void;
  onSaved: () => void;
}

const PRESET_COLORS = [
  '#7c3aed', '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#06b6d4', '#84cc16', '#f97316', '#ec4899',
];

export default function TeamModal({
  workArea,
  team,
  onClose,
  onSaved,
}: TeamModalProps): React.JSX.Element {
  const isEditing = !!team;
  const [name, setName] = useState(team?.name || '');
  const [color, setColor] = useState(team?.work_area_color || workArea.workAreaColor || '#7c3aed');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      toast.warning('Bitte Team-Namen eingeben');
      return;
    }

    setSaving(true);
    try {
      if (isEditing && team) {
        await PlantafelService.updateTeam(team.id, {
          name: name.trim(),
          color,
          sort_order: sortOrder,
          is_active: isActive,
        });
        toast.success('Team aktualisiert');
      } else {
        await PlantafelService.createTeam({
          name: name.trim(),
          work_area: workArea.workAreaId,
          color,
          sort_order: sortOrder,
          is_active: isActive,
        });
        toast.success('Team erstellt');
      }
      onSaved();
    } catch (err: any) {
      pdebugError('Failed to save team:', err);
      toast.error(err?.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }, [name, color, sortOrder, isActive, isEditing, team, workArea.workAreaId, onSaved]);

  const handleDelete = useCallback(async () => {
    if (!team) return;
    if (!window.confirm('Team wirklich löschen? Dies ist nur möglich, wenn keine Schichten zugeordnet sind.')) {
      return;
    }

    setDeleting(true);
    try {
      await PlantafelService.deleteTeam(team.id);
      toast.success('Team gelöscht');
      onSaved();
    } catch (err: any) {
      pdebugError('Failed to delete team:', err);

      // Handle BE-OPS-R9-3: 409 with team_has_shifts_use_deactivate
      if (err?.response?.status === 409) {
        const errorCode = err?.response?.data?.error;
        const shiftsCount = err?.response?.data?.shifts_count || 0;

        if (errorCode === 'team_has_shifts_use_deactivate') {
          const shouldDeactivate = window.confirm(
            `Das Team hat ${shiftsCount} zugeordnete Schicht${shiftsCount !== 1 ? 'en' : ''} und kann nicht gelöscht werden.\n\nMöchten Sie das Team stattdessen deaktivieren?`
          );

          if (shouldDeactivate) {
            try {
              await PlantafelService.updateTeam(team.id, { is_active: false });
              toast.success('Team deaktiviert');
              onSaved();
            } catch (deactivateErr) {
              pdebugError('Failed to deactivate team:', deactivateErr);
              toast.error('Fehler beim Deaktivieren des Teams');
            }
          }
          setDeleting(false);
          return;
        }
      }

      const msg = err?.response?.data?.message || err?.response?.data?.detail || 'Fehler beim Löschen';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  }, [team, onSaved]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '12px',
          width: '400px',
          maxWidth: '90vw',
          boxShadow: shadows.elevated,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: colors.navy }}>
              {isEditing ? 'Team bearbeiten' : 'Neues Team'}
            </div>
            <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px' }}>
              WorkArea: {workArea.workAreaName}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: colors.textMuted,
            }}
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: colors.textMuted,
                marginBottom: '6px',
              }}
            >
              Team Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Team 1, Montage A"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
              }}
              autoFocus
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: colors.textMuted,
                marginBottom: '6px',
              }}
            >
              Farbe
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '10px',
                    background: c,
                    border: color === c ? `3px solid ${colors.navy}` : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'transform 0.1s',
                  }}
                />
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: colors.textMuted,
                marginBottom: '6px',
              }}
            >
              Sortierung
            </label>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
              min={0}
              style={{
                width: '100px',
                padding: '10px 12px',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                }}
              />
              <span style={{ fontSize: '14px', color: colors.navy }}>Aktiv</span>
            </label>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isEditing ? 'space-between' : 'flex-end',
            padding: '16px 20px',
            borderTop: `1px solid ${colors.border}`,
            gap: '12px',
          }}
        >
          {isEditing && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                border: `1px solid ${colors.danger}`,
                borderRadius: '8px',
                background: colors.bgCard,
                color: colors.danger,
                fontSize: '14px',
                fontWeight: 500,
                cursor: deleting ? 'not-allowed' : 'pointer',
              }}
            >
              <FaTrash size={12} />
              {deleting ? 'Löschen...' : 'Löschen'}
            </button>
          )}

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                ...ps.buttonSecondary,
              }}
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                ...(saving ? ps.buttonDisabled : ps.buttonPrimary),
              }}
            >
              <FaSave size={12} />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
